import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Plus } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { buildItemSubtitle } from "@/pages/postloss/modules/components/itemSubtitles";
import { AccountRow } from "@/pages/postloss/modules/components/AccountRow";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

import {
  validateSafeDepositFields,
  safeDepositToItemFields,
  parseSafeDepositToForm,
  type SafeDepositFormValues,
  type SafeDepositFieldErrors,
} from "./SafeDepositForm";
import { EditDepositBoxDialog } from "./EditDepositBoxDialog";

export const SafeDepositReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  setAnswer,
  answers,
}) => {
  const { person } = useAppContext();
  const firstName = person?.first_name ?? "they";
  const estateId = person?.estate_id;
  const { assets, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, isDeletingItem } =
    useEstateItems(estateId);

  const depositItems = useMemo(
    () => assets.filter((a) => a.category === "Safe or safety deposit box"),
    [assets],
  );

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (
      depositItems.length === 0 &&
      !hasRedirected.current &&
      answers["safe_deposit_review"] !== "has_items"
    ) {
      hasRedirected.current = true;
      goToScreen("has_safe_deposit", {
        ...answers,
        safe_deposit_review: "none",
      });
    }
  }, [depositItems.length, isLoading, isFetching, goToScreen, answers]);

  const [editing, setEditing] = useState<{
    item: EstateItem;
    form: SafeDepositFormValues;
  } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editFieldErrors, setEditFieldErrors] = useState<SafeDepositFieldErrors>({});

  const handleSaveEdit = async () => {
    if (!editing) return;
    const errors = validateSafeDepositFields(editing.form);
    if (Object.keys(errors).length > 0) {
      setEditFieldErrors(errors);
      return;
    }
    try {
      const fields = safeDepositToItemFields(editing.form);
      await updateItem(editing.item.id, fields);
      setEditing(null);
      setEditError(null);
      setEditFieldErrors({});
    } catch {
      setEditError("Failed to update. Please try again.");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!editing) return;
    try {
      await deleteItem(editing.item.id);
      setEditing(null);
    } catch {
      setEditError("Failed to delete. Please try again.");
    }
  };

  const handleAddAnother = () => {
    setAnswer("safe_deposit_review", "");
    goToScreen("add_safe_deposit", {
      ...answers,
      has_safe_deposit: "yes",
      safe_deposit_review: "",
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h2>Review {firstName}&apos;s deposit boxes</h2>
        <p className="mt-2 text-secondary">Edit, remove, or add anything that&apos;s missing.</p>
      </div>

      <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 rounded-lg border border-primary bg-white p-6">
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold">Deposit boxes</p>
          <button
            type="button"
            onClick={handleAddAnother}
            className="flex items-center gap-1 text-sm text-secondary hover:text-primary"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
        {depositItems.map((item) => (
          <AccountRow
            key={item.id}
            item={item}
            subtitle={buildItemSubtitle(item) || undefined}
            onEdit={(editedItem) => {
              setEditError(null);
              setEditFieldErrors({});
              setEditing({ item: editedItem, form: parseSafeDepositToForm(editedItem) });
            }}
          />
        ))}
      </div>

      <Button
        variant="brand"
        rounded
        className="mx-auto w-full max-w-[400px]"
        onClick={() => {
          setAnswer("safe_deposit_review", "has_items");
          onComplete();
        }}
      >
        Continue <ArrowRight className="h-4 w-4" />
      </Button>

      <EditDepositBoxDialog
        editing={editing ? { form: editing.form } : null}
        error={editError}
        fieldErrors={editFieldErrors}
        isLoading={isUpdatingItem || isDeletingItem}
        onChange={(form) => {
          setEditError(null);
          setEditFieldErrors({});
          setEditing((prev) => (prev ? { ...prev, form } : null));
        }}
        onSave={handleSaveEdit}
        onClose={() => setEditing(null)}
        onDeleteConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
